/**
 * The Inbox's neutral row — `tasks/inbox.md` §3.
 *
 * ONE shape for every source. The page draws a row without knowing which table it came from, which
 * is what makes adding incoming referrals or inbound email a server-side change with an icon
 * rather than a template rewrite.
 *
 * ── NOTHING IN THE CLIENT BRANCHES ON `state` ────────────────────────────────────────
 *
 * `state` is the SOURCE's own vocabulary — `'awaiting_review'`, `'sent'`, and whatever a referral
 * table calls its states — and it is carried for chips, tooltips and `data-test` hooks only.
 * `stateLabel` is what a human reads and the server decides it.
 *
 * That division is deliberate. The day a source has a state this build has never heard of, the row
 * must still draw: a `switch` over `state` in a template is a blank row waiting to happen, and it
 * would have to be edited in lockstep with every server-side source that is ever added.
 */

/** What may be DONE to a row, as the server computed it for THIS caller. */
export interface InboxItemActions {
  /**
   * §26 stage one / stage two, resolved against the document's own permission lists by
   * `utils/patientDocumentReviewPermissions.js` — the single implementation, which the client
   * reads rather than copies.
   *
   * OPTIONAL, and every reader must treat ABSENT as PERMITTED (`!== false`, never `=== true`). A
   * server that has not shipped the permission lists sends neither, and a client reading that as
   * "forbidden" would disable every action in the product. `patient-document-request.service.ts`
   * carries the same rule for the same fields.
   */
  canSignOff?: boolean;
  canValidate?: boolean;
  /**
   * `false` on a PERSONAL item (a task assigned to the viewer): the Flagged tab is practice-wide, so the
   * server refuses to flag it and the row draws no flag button. Absent means flaggable.
   */
  canFlag?: boolean;
}

/**
 * The one button a row offers, as a SOURCE other than `document` describes it.
 *
 * `key` is the source's own verb ("acknowledge"), used for `data-test-action` and by the type's opener;
 * `label` is what the button says. Documents do not send this: their two-stage rule
 * (`inboxItemAction`) predates it and stays the single implementation of that rule.
 */
export interface InboxItemPrimaryAction {
  key: string;
  label: string;
}

export interface InboxItemDto extends InboxItemActions {
  /** Which source produced this row. The key into `INBOX_ITEM_TYPES`. */
  itemType: string;
  /** The SOURCE row's id — a `PatientDocumentRequests.id` for a document. */
  sourceId: string;
  groupId: string;

  patientId?: string | null;
  /** "Surname, First name", decrypted server-side. Null when the row names no patient. */
  patientName?: string | null;
  patientNumber?: string | null;

  /** The document's name, a referral's subject, an email's subject. */
  title: string;
  /** Second line. NULL rather than '' so the template can skip the element entirely. */
  subtitle?: string | null;

  /**
   * WHEN THIS BECAME RELEVANT, and it is not one column: a returned document is dated by when it
   * came back, an outstanding one by when it went out. The server decides which; the client only
   * groups by it.
   *
   * Never null on a row the server served — `tasks/inbox.md` §3.2.
   */
  occurredAt?: string | null;

  /** The source's own vocabulary. NOT branched on — see the header. */
  state?: string | null;
  /** What a human reads. "Awaiting sign-off", "Sent back", "Awaiting patient". */
  stateLabel?: string | null;
  /** §25. Only ever set on a skipped ask, which no tab shows today. */
  skipReason?: string | null;

  reviewedByName?: string | null;
  validatedByName?: string | null;

  /** From the state table. Null on the overwhelming majority of items, which have no row at all. */
  flaggedAt?: string | null;
  flaggedByName?: string | null;
  flagNote?: string | null;

  patientInviteId?: string | null;
  sentMediaFileName?: string | null;

  /**
   * The row's action for sources other than `document`, computed server-side for THIS caller and
   * THIS tab. Null or absent: the row offers no button (it still opens). See `inboxPrimaryAction`.
   */
  primaryAction?: InboxItemPrimaryAction | null;
}

/** The four tabs, in the order they are drawn. Mirrors `utils/inboxSources/index.js`'s `TABS`. */
export type InboxTab = 'new' | 'for-review' | 'flagged' | 'pending';

export const INBOX_TABS: InboxTab[] = ['new', 'for-review', 'flagged', 'pending'];

/**
 * The tab labels.
 *
 * "New" and "For Review" are the two halves of the two-stage document review and they are NOT
 * collapsed: stage one asks a person "is this the right document, complete and legible", stage two
 * asks a clinician to put their name to it on the patient's record. Different questions, different
 * people, and a single queue would hide which one an item is waiting for.
 */
export const INBOX_TAB_LABELS: Record<InboxTab, string> = {
  'new': 'New',
  'for-review': 'For Review',
  'flagged': 'Flagged',
  'pending': 'Pending',
};

/** One page of one tab, as `GET /api/inbox/group/:groupId` serves it. */
export interface InboxPage {
  success: boolean;
  data: InboxItemDto[];
  tab: InboxTab;
  /**
   * Opaque. Echo it back verbatim or not at all — it is a keyset position, not an offset, and its
   * contents are deliberately not part of the contract (`tasks/inbox.md` §3.3).
   */
  nextCursor?: string | null;
  hasMore?: boolean;
  limit?: number;
  /**
   * What the SERVER actually matched a search against. Patient names are encrypted with no blind
   * index, so no `WHERE` can reach them — the page says so rather than implying it searched
   * everything. See `inboxSearchHint`.
   */
  searchedFields?: string[];
}

export interface InboxCounts {
  'new': number;
  'for-review': number;
  flagged: number;
  pending: number;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE TYPE ICON
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

export interface InboxItemTypeDescriptor {
  /** A Material icon ligature. */
  icon: string;
  /** What the icon means, for the tooltip and the aria-label. */
  label: string;
  /** Scopes the colour in `inbox.component.scss`. */
  cssClass: string;
}

/**
 * ── WHY THIS MAP EXISTS HERE AND NOT IN `shared/` ────────────────────────────────────
 *
 * There is no shared record-type → icon helper anywhere in this application. Every list screen
 * hand-rolls its `<mat-icon>`s inline, the clinical timeline included. Inventing a general one for
 * the whole product is a bigger change than this feature justifies and would have exactly one
 * caller on the day it landed.
 *
 * So this map is scoped to INBOX ITEM TYPES specifically, which is a real closed vocabulary with a
 * server-side registry behind it (`utils/inboxSources/index.js`) — not a guess at what every
 * screen might want.
 *
 * ── THE DOCUMENT ICON IS THE TIMELINE'S ICON, DELIBERATELY ───────────────────────────
 *
 * `assignment_turned_in` is what `patient-clinical-timeline.component.html` already draws for a
 * returned document. The same thing looks the same in both places, so somebody who signs a document
 * off in the Inbox recognises it on the patient's record afterwards.
 *
 * ── AN UNKNOWN TYPE STILL DRAWS ──────────────────────────────────────────────────────
 *
 * `inboxItemType()` falls back rather than returning undefined. A server that registers a source
 * this build has not shipped an icon for must produce a row with a generic icon, not a broken one
 * or an empty cell — the same reason nothing branches on `state`.
 */
export const INBOX_ITEM_TYPES: Record<string, InboxItemTypeDescriptor> = {
  document: {
    icon: 'assignment_turned_in',
    label: 'Returned document',
    cssClass: 'document-icon',
  },
  // External clinicians' notes waiting for "Validate to timeline" (tasks/referrals.md §5.5,
  // utils/inboxSources/referralNoteSource.js).
  'referral-note': {
    icon: 'rate_review',
    label: 'External note',
    cssClass: 'referral-note-icon',
  },
  // Referrals from another MedicAI practice waiting to be accepted or declined (referrals architecture §19,
  // utils/inboxSources/referralIncomingSource.js).
  'referral-incoming': {
    icon: 'move_to_inbox',
    label: 'Incoming referral',
    cssClass: 'referral-note-icon',
  },
  // Entries another MedicAI practice shared on an accepted referral, waiting for "Validate to timeline" (§19, P7,
  // utils/inboxSources/referralPracticeEntrySource.js).
  'referral-practice-entry': {
    icon: 'domain_verification',
    label: 'Entry from a practice',
    cssClass: 'referral-note-icon',
  },
  // A practice that accepted this practice's referral referred the patient onward (§19, P9).
  'referral-onward-notice': {
    icon: 'forward_to_inbox',
    label: 'Onward referral',
    cssClass: 'referral-note-icon',
  },
  // A referring practice changed a referred patient's details, reviewed field by field (§19, P10–P12).
  'referral-demographics': {
    icon: 'manage_accounts',
    label: 'Patient details changed',
    cssClass: 'referral-note-icon',
  },
  // A pending task assigned to the viewer — doctors only, and only their own (tasks/task-assignment.md,
  // utils/inboxSources/taskSource.js).
  task: {
    icon: 'task_alt',
    label: 'Assigned task',
    cssClass: 'referral-note-icon',
  },
  // Another MedicAI practice asking to link: the practice OWNER's alone (tasks/referral-invite-unified.md,
  // utils/inboxSources/referralLinkRequestSource.js). No patient.
  'referral-link-request': {
    icon: 'handshake',
    label: 'Practice link request',
    cssClass: 'referral-note-icon',
  },
  // Something the patient shared from the patient app, to add to the timeline as "Patient provided"
  // (tasks/patient-app.md §6.5, utils/inboxSources/patientAppEntrySource.js).
  'patient-app-entry': {
    icon: 'phonelink_ring',
    label: "From the patient's app",
    cssClass: 'referral-note-icon',
  },
  // The patient changed their details in the patient app, reviewed field by field (§5.2).
  'patient-app-demographics': {
    icon: 'manage_accounts',
    label: 'Details changed in the patient app',
    cssClass: 'referral-note-icon',
  },
  // Allergies, medical history, medications or doctors the patient added in the app, filed item by item.
  'patient-app-history': {
    icon: 'medical_information',
    label: 'Medical history from the patient app',
    cssClass: 'referral-note-icon',
  },
  // email / connect-message / whatsapp land here as their adapters ship.
};

/** The generic row. See the header — an unregistered type draws, it does not break. */
export const INBOX_ITEM_TYPE_FALLBACK: InboxItemTypeDescriptor = {
  icon: 'inbox',
  label: 'Inbox item',
  cssClass: 'generic-icon',
};

export function inboxItemType(itemType: string | null | undefined): InboxItemTypeDescriptor {
  if (!itemType) return INBOX_ITEM_TYPE_FALLBACK;
  return INBOX_ITEM_TYPES[itemType] || INBOX_ITEM_TYPE_FALLBACK;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * HELPERS THE PAGE AND ITS SPEC BOTH USE
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * ABSENT MEANS PERMITTED. `!== false`, never `=== true`.
 *
 * See `InboxItemActions`. This exists as a function rather than as `row.canSignOff !== false`
 * inline in a template so that the rule has one place to be got right, and so the spec can assert
 * it directly.
 */
export function canSignOff(item: InboxItemDto | null | undefined): boolean {
  return !!item && item.canSignOff !== false;
}

export function canValidate(item: InboxItemDto | null | undefined): boolean {
  return !!item && item.canValidate !== false;
}

/**
 * Which action a row offers, from the tab it is on.
 *
 * The TAB decides the stage, not the row's `state` — a row is in `new` because it is awaiting
 * sign-off, and the button follows from that. On `flagged` the row can be at either stage, so the
 * item's own `state` is the only thing that can say, and this is the one place it is read.
 */
export function inboxItemAction(item: InboxItemDto, tab: InboxTab): 'signOff' | 'validate' | null {
  if (tab === 'new') return canSignOff(item) ? 'signOff' : null;
  if (tab === 'for-review') return canValidate(item) ? 'validate' : null;
  if (tab === 'flagged') {
    if (item.state === 'awaiting_review') return canSignOff(item) ? 'signOff' : null;
    if (item.state === 'accepted') return canValidate(item) ? 'validate' : null;
  }
  // `pending` offers no review action: nothing has come back to review.
  return null;
}

/** What the document actions say on the button. */
export const INBOX_DOCUMENT_ACTION_LABELS: Record<'signOff' | 'validate', string> = {
  signOff: 'Sign off',
  validate: 'Validate',
};

/**
 * The button a row offers, for ANY item type.
 *
 * `document` goes through `inboxItemAction` unchanged — the two-stage review rule has one
 * implementation and this does not restate it. Every other type draws what its server source sent in
 * `primaryAction`, so a new source gets its button without a client rule of its own.
 *
 * A malformed `primaryAction` (no key or no label) is treated as none: a button with no words, or one
 * that cannot be told apart in `data-test-action`, is worse than no button.
 */
export function inboxPrimaryAction(item: InboxItemDto | null | undefined, tab: InboxTab): InboxItemPrimaryAction | null {
  if (!item) return null;
  if (item.itemType === 'document') {
    const action = inboxItemAction(item, tab);
    return action ? { key: action, label: INBOX_DOCUMENT_ACTION_LABELS[action] } : null;
  }
  const action = item.primaryAction;
  if (!action || typeof action.key !== 'string' || !action.key.trim()
    || typeof action.label !== 'string' || !action.label.trim()) {
    return null;
  }
  return { key: action.key, label: action.label };
}

/**
 * The group-room socket events, PER ITEM TYPE, that mean "one of this type's items may have changed
 * tab". `InboxService` reloads the open tab on any of them — see its header for why it reloads rather
 * than patching.
 *
 * Per type so that adding a source is an entry here rather than an edit to a flat list whose members
 * nobody can attribute. `inboxReloadEvents()` is what the service subscribes to.
 */
export const INBOX_RELOAD_EVENTS: Record<string, string[]> = {
  document: [
    'patientDocumentRequested',
    'patientDocumentReturned',
    'patientDocumentRevoked',
    'patientDocumentReviewed',
    'patientDocumentValidated',
  ],
  // Emitted by services/referralIntegration (a note arrived) and controllers/referralController.js (reviewed).
  'referral-note': [
    'referralNoteReceived',
    'referralNoteReviewed',
  ],
  // Emitted by services/referralIntegration/practice.js (a referral arrived) and controllers/referralController.js (decided).
  'referral-incoming': [
    'referralIncomingReceived',
    'referralIncomingDecided',
  ],
  // Emitted by controllers/referralController.js when an entry is validated or dismissed.
  'referral-practice-entry': [
    'referralPracticeEntryReviewed',
  ],
  'referral-onward-notice': [
    'referralOnwardNoticeAcknowledged',
  ],
  'referral-demographics': [
    'referralDemographicsReviewed',
  ],
  // Emitted by controllers/referralController.js to the asked practice's room when a request arrives or is answered.
  'referral-link-request': [
    'referralLinkRequested',
    'referralLinkAnswered',
  ],
  // Emitted by services/patientAppIntegration (arrived) and controllers/patientAppController.js (reviewed).
  'patient-app-entry': [
    'patientAppEntryReceived',
    'patientAppEntryReviewed',
  ],
  'patient-app-demographics': [
    'patientAppDetailsProposed',
    'patientAppDetailsReviewed',
  ],
  'patient-app-history': [
    'patientAppHistoryProposed',
    'patientAppHistoryReviewed',
  ],
  // Emitted by controllers/patientTasksController.js on every task write, to the practice room.
  task: [
    'taskAdded',
    'taskUpdated',
    'taskDeleted',
  ],
};

/**
 * Whether a task socket event can have changed THIS viewer's Inbox (`tasks/task-assignment.md`).
 *
 * Task events go to the whole practice on every task write, including every status tick on the task list.
 * Reloading every connected Inbox for each of them would be N count queries per click for items only a
 * doctor who is an assignee can ever see. So:
 *   · a non-doctor's Inbox never holds tasks — never relevant;
 *   · a task that names assignees, or one already on screen, may have entered or left — relevant;
 *   · anything else (an unassigned task, not on screen) cannot be in this Inbox — not relevant.
 *
 * Known gap, accepted: a task assigned to me whose assignees are then ALL removed, while my Inbox has not
 * loaded it, leaves my badge one high until the next Inbox event or page load.
 */
export function isTaskEventRelevant(
  payload: any,
  viewer: { id?: string | null; userType?: string | null } | null,
  loadedSourceIds: Set<string>,
): boolean {
  if (!viewer || viewer.userType !== 'doctor') return false;
  if (Array.isArray(payload?.assignees) && payload.assignees.length > 0) return true;
  return !!payload?.id && loadedSourceIds.has(String(payload.id).toLowerCase());
}

/** Every reload event across all types, de-duplicated, in declaration order. */
export function inboxReloadEvents(): string[] {
  const events: string[] = [];
  for (const list of Object.values(INBOX_RELOAD_EVENTS)) {
    for (const event of list) {
      if (!events.includes(event)) events.push(event);
    }
  }
  return events;
}

/**
 * The date a tab GROUPS its rows by.
 *
 * Flagged is ordered by when things were FLAGGED — "what did we flag, most recent first" is the
 * question that tab answers — so its headers follow the flag and not the item's own date. Every
 * other tab groups by the item's date. Getting this wrong produces a list whose headers disagree
 * with its order, which reads as rows in random places.
 */
export function inboxGroupDate(item: InboxItemDto, tab: InboxTab): string | null {
  const value = tab === 'flagged' ? (item.flaggedAt || item.occurredAt) : item.occurredAt;
  return value || null;
}

/**
 * What the search box promises, in the words of what the server actually matched.
 *
 * Patient names are encrypted with no blind index (`models/patient.js`), so a name search narrows
 * the LOADED PAGE and not the practice. Saying so is the whole point: a search box that silently
 * misses matches is worse than one that states its scope.
 */
export function inboxSearchHint(page: InboxPage | null | undefined): string {
  const fields = page && page.searchedFields ? page.searchedFields : [];
  if (!fields.length) return 'Search by document, patient number or name';
  return `Searching ${fields.join(' and ')} across the practice; names within this page`;
}
